import React, { useState, useCallback, useRef } from 'react';
import { Upload, Sparkles, Clock, CheckCircle, AlertCircle, Wand2 } from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { authenticatedFormDataFetch } from '../utils/apiUtils';
import StatsWidget from '../components/StatsWidget';
import { RecentGenerationsWidget, HowItWorksButton } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { validateImageFile } from '../utils/fileValidation';
import ImagePreview from '../components/ImagePreview';
import { createImagePreview } from '../utils/imagePreview';
import { useCredits } from '../contexts/CreditContext';
import { getImageCredits } from '../config/subscriptionPlans';

interface ElementReplacementRequest {
  id: string;
  prompt: string;
  outputFormat: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  image?: string;
  result?: string;
}

const ReplaceElements: React.FC = () => {
  const { user } = useAuth();
  const { refreshCredits, creditBalance } = useCredits();
  const { showSuccess, showError, showWarning } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState('Replace the floor for a modern black mirror floor');
  const [outputFormat, setOutputFormat] = useState('jpg');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [results, setResults] = useState<any[]>([]);
  const [processingTime, setProcessingTime] = useState<number>(0);
  const [requests, setRequests] = useState<ElementReplacementRequest[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateImageFile(file, 10);
      if (!validation.isValid) {
        showWarning(validation.error!);
        return;
      }
      
      try {
        setSelectedFile(file);
      } catch (error) {
        console.error('Error processing file:', error);
        showError('Error processing file. Please try again.');
      }
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length === 0) return;

    const file = files[0]; // Take first file

    // Validate file type and size
    const validation = validateImageFile(file, 10);
    if (!validation.isValid) {
      showWarning(validation.error!);
      return;
    }

    try {
      setSelectedFile(file);
    } catch (error) {
      console.error('Error processing file:', error);
      showError('Error processing file. Please try again.');
    }
  }, [showWarning, showError]);

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedFile) {
      showWarning('Please select an image to transform.');
      return;
    }

    // Check if user has enough credits before processing
    const creditsNeeded = getImageCredits(1); // 40 credits per image
    if (creditBalance && creditBalance.displayCreditsRemaining < creditsNeeded) {
      showError(
        `Insufficient credits! You need more credits. Please upgrade your plan.`
      );
      return;
    }

    setIsProcessing(true);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('prompt', prompt);
      formData.append('outputFormat', outputFormat);

      const response = await authenticatedFormDataFetch('/api/v1/replace-elements', formData);

      const result = await response.json();

      if (result.success) {
        // Set results for immediate display
        setResults([{
          originalImage: result.data.originalImage,
          replacedImage: result.data.replacedImage,
          prompt: prompt,
          outputFormat: outputFormat
        }]);
        setProcessingTime(result.data.processingTime || 0);

        // Add new request to the list
        const newRequest: ElementReplacementRequest = {
          id: Date.now().toString(),
          prompt,
          outputFormat,
          status: 'completed',
          createdAt: new Date().toISOString(),
          image: result.data.originalImage,
          result: result.data.replacedImage
        };
        setRequests(prev => [newRequest, ...prev]);

        // Trigger refresh of RecentGenerationsWidget
        setRefreshTrigger(prev => prev + 1);

        await refreshCredits();

        // Reset form after successful generation
        setSelectedFile(null);
        
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        showError(`Transformation failed: ${result.message || result.error}`);
      }
    } catch (error) {
      console.error('Transformation error:', error);
      showError('Transformation failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'processing':
        return <Sparkles className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with How It Works Button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">🎨 Replace Elements</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Transform images by replacing elements or changing styles using AI
          </p>
        </div>
        <HowItWorksButton variant="outline" />
      </div>

      {/* Stats Widget */}
      <StatsWidget
        modelType="element_replacement"
        title=""
        description=""
        userId={user?.id}
      />

      {/* Form - Matching home.html structure exactly */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload - EXACTLY as in home.html */}
          <div className="form-group">
            <label htmlFor="replaceElementsImage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              📸 Image to Transform (JPG/PNG/WebP/HEIC)
            </label>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              id="replaceElementsImage"
              accept="image/jpeg,image/png,image/webp,image/heic"
              onChange={handleFileSelect}
              className="hidden"
              required
            />

            {/* Modern drag & drop area */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${isDragOver
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={openFileDialog}
            >
              <div className="flex flex-col items-center space-y-4">
                <div className={`p-3 rounded-full ${isDragOver ? 'bg-purple-100 dark:bg-purple-800' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                  <Upload className={`h-8 w-8 ${isDragOver ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'
                    }`} />
                </div>

                <div>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {isDragOver ? 'Drop image here' : 'Click to select or drag & drop'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Upload an image where you want to replace elements or change the style
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Supports JPG, PNG, WebP, and HEIC formats
                  </p>
                </div>

                {!isDragOver && (
                  <button
                    type="button"
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFileDialog();
                    }}
                  >
                    Choose File
                  </button>
                )}
              </div>
            </div>

            <small className="text-gray-500 dark:text-gray-400 mt-1 block">
              Upload an image where you want to replace elements or change the style
            </small>
          </div>

          {/* File Preview */}
          {selectedFile && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Selected Image to Transform
              </h3>
              <ImagePreview
                file={selectedFile}
                onRemove={() => setSelectedFile(null)}
                alt="Selected image to transform"
                className="w-64 h-48 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600"
              />
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)}MB)
              </p>
            </div>
          )}

          {/* Transformation Prompt - EXACTLY as in home.html */}
          <div className="form-group">
            <label htmlFor="replacePrompt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              🎨 Transformation Prompt
            </label>
            <textarea
              id="replacePrompt"
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe how you want to transform the image..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <small className="text-gray-500 dark:text-gray-400 mt-1 block">
              Describe the style, theme, or elements you want to replace (e.g., "Make this a cyberpunk scene", "Transform into watercolor painting")
            </small>
          </div>

          {/* Output Format - EXACTLY as in home.html */}
          <div className="form-group" style={{ display: 'none' }}>
            <label htmlFor="outputFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              📁 Output Format
            </label>
            <select
              id="outputFormat"
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="jpg" selected>JPEG (JPG)</option>
              <option value="png">PNG</option>
              <option value="webp">WebP</option>
            </select>
            <small className="text-gray-500 dark:text-gray-400 mt-1 block">
              Choose the output format for your transformed image
            </small>
          </div>

          {/* Submit Button - EXACTLY as in home.html */}
          <button
            type="submit"
            disabled={!selectedFile || isProcessing}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
            ) : (
              '🎨 Transform Image'
            )}
          </button>
        </form>
      </div>

      {/* Recent Generations Widget */}
      <RecentGenerationsWidget
        userId={user?.id}
        title="Element Replacement Generations"
        description="View your latest element replacement transformations with before/after comparisons"
        showFilters={false}
        maxItems={10}
        className="mt-6"
        modelTypeFilter="element_replacement"
        refreshTrigger={refreshTrigger}
      />
    </div>
  );
};

export default ReplaceElements;
